import { useEffect } from 'react'
import { useNavigate } from '@tanstack/react-router'
import type { InferResponseType } from 'hono/client'

import { apiClient } from '@/apiClient'

// Type for the single document response from the API
type DocumentWithDetails = InferResponseType<
  (typeof apiClient.documents)[':documentId']['$get']
>['data']

interface DocumentViewSearch {
  mode?: 'view' | 'modify'
  pageNum?: number
  previewMode?: 'image' | 'markdown'
  tab?: 'details' | 'preview' | 'attachments' | 'logs'
}

export function useDocumentNavigation(
  search: DocumentViewSearch,
  document: DocumentWithDetails | undefined,
  isMobile: boolean
) {
  const { pageNum = 1, previewMode = 'image', tab } = search
  const navigate = useNavigate({ from: '/documents/$documentId' })

  // Handle tab correction when switching between mobile/desktop
  useEffect(() => {
    if (!isMobile && tab === 'details') {
      navigate({
        search: { ...search, tab: 'preview' },
        replace: true,
      })
    }
  }, [isMobile, tab, navigate, search])

  const goPrev = () => {
    navigate({
      search: {
        ...search,
        pageNum: Math.max(pageNum - 1, 1),
        previewMode,
      },
      replace: true,
    })
  }

  const goNext = () => {
    if (!document?.num_pages) return

    navigate({
      search: {
        ...search,
        pageNum: Math.min(pageNum + 1, document.num_pages),
        previewMode,
      },
      replace: true,
    })
  }

  const setPreviewMode = (newPreviewMode: typeof previewMode) =>
    navigate({
      search: {
        ...search,
        pageNum,
        previewMode: newPreviewMode,
      },
      replace: true,
    })

  const handleTabChange = (tab: string) => {
    // On desktop, redirect 'details' tab to 'preview' since details are always visible
    const targetTab = !isMobile && tab === 'details' ? 'preview' : tab
    navigate({
      search: { ...search, tab: targetTab as DocumentViewSearch['tab'] },
    })
  }

  return {
    goPrev,
    goNext,
    setPreviewMode,
    handleTabChange,
  }
}
